// Client for the editable-Markdown routes (docs/files-markdown.md). Uses the app's same-origin JSON client
// (session cookie + CSRF); errors surface as TransportError with `kind`, `status` and server `code`
// (not_markdown, markdown_too_large, version_conflict, storage_quota_exceeded, task_deleted, …).
import { api } from '@/api/client';

export interface MdVersion {
  file_id: string; version_no: number; sha256: string; size: number;
  created_at: string; updated_at: string; created_by: string; updated_by: string;
  is_original: boolean; is_current: boolean; restored_from: number | null;
}
export interface MdContent extends MdVersion { content: string }
export interface MdSaveResult extends MdVersion { result: 'created' | 'merged' | 'unchanged'; file: Record<string, unknown> }

/** Port used by the autosave session (the HTTP adapter below; tests pass a fake). */
export interface MarkdownApi {
  getContent(fileId: string): Promise<MdContent>;
  putContent(fileId: string, content: string, baseVersion: number): Promise<MdSaveResult>;
  listVersions(fileId: string): Promise<MdVersion[]>;
  getVersion(fileId: string, n: number): Promise<MdContent>;
  restoreVersion(fileId: string, n: number, baseVersion?: number): Promise<MdSaveResult>;
}

const enc = encodeURIComponent;

export function httpMarkdownApi(workspaceId: string): MarkdownApi {
  const base = (fileId: string) => `/workspaces/${enc(workspaceId)}/files/${enc(fileId)}`;
  return {
    getContent: fileId => api<MdContent>('GET', base(fileId) + '/content'),
    putContent: (fileId, content, baseVersion) => api<MdSaveResult>('PUT', base(fileId) + '/content', { content, base_version: baseVersion }),
    listVersions: async fileId => (await api<{ versions: MdVersion[] }>('GET', base(fileId) + '/versions')).versions,
    getVersion: (fileId, n) => api<MdContent>('GET', base(fileId) + '/versions/' + enc(String(n))),
    restoreVersion: (fileId, n, baseVersion) => api<MdSaveResult>('POST', base(fileId) + '/versions/' + enc(String(n)) + '/restore', baseVersion === undefined ? {} : { base_version: baseVersion }),
  };
}

/** `*.md` / `*.markdown`, case-insensitive (reference `openTitle/onOpen`). The server additionally requires text content. */
export const isMarkdownName = (name: string): boolean => /\.(md|markdown)$/i.test(name);
